import React, { useEffect, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import SyncConstants from "../../constants/SyncConstants";
import PersonLabelWithDynamicInfo from "../Team/PersonLabelWithDynamicInfo";
import moment from "moment";
import stylelist from "stylelist";
import SlideToConfirm from "../UI/SlideToConfirm";
import useAuthentication from "../../hooks/useAuthentication";
import { generateRandomClassName } from "../../libs/StringUtil";
import { set_notification } from "../../actions/Sync";
import { add_entry } from "../../actions/ExpenseBucket";

const ExternalCostsList = (props) => {

    const UserSettings = useSelector((state) => state.User);
    const ExpenseBucket = useSelector((state) => state.ExpenseBucket);
    const [serverData, setServerData] = useState({});
    const [expandedDescriptions, setExpandedDescriptions] = useState([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [entryToDelete, setEntryToDelete] = useState(null);
    const [deleteCount, setDeleteCount] = useState(0);
    const [skipSliderForSession, setSkipSliderForSession] = useState(false);
    const Auth = useAuthentication();
    const dispatch = useDispatch();
    const randomClassName = generateRandomClassName();

    const init = useEffect(() => {
        loadFromServer();
    }, []);

    useEffect(() => {
        loadFromServer();
    }, [props.filterDateFrom, props.filterDateTo, props.filterPerson]);

    const loadFromServer = () => {
        axios({
            method: "get",
            auth: Auth.getAuthObject(),
            url: SyncConstants["syncBaseURL"] + "externalCosts/byProject",
            params: {
                project: props.project,
                filter: JSON.stringify({
                    dateFrom: props.filterDateFrom,
                    dateTo: props.filterDateTo,
                    person: props.filterPerson
                })
            },
            responseType: 'json'
        }).then((response) => {
            setServerData(response.data);
        }).catch(response => {
        });
    }

    const deleteEntry = (entryId) => {
        axios({
            method: "delete",
            auth: Auth.getAuthObject(),
            url: SyncConstants["syncBaseURL"] + "externalCost",
            responseType: 'json',
            data: { entryId: entryId }
        }).then((response) => {
            loadFromServer();
            dispatch(set_notification("Erfolgreich gelöscht", "success"));
            setDeleteCount(prev => prev + 1);
        }).catch(response => {
            dispatch(set_notification("Eintrag konnte nicht gelöscht werden", "error"));
        });
    }

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    const toggleDescriptionExpand = (entryId) => {
        setExpandedDescriptions(prev => {
            if (prev.includes(entryId)) {
                return prev.filter(id => id !== entryId);
            } else {
                return [...prev, entryId];
            }
        });
    };

    const handleDeleteClick = (entry) => {
        if (skipSliderForSession) {
            deleteEntry(entry.id);
        } else {
            setEntryToDelete(entry);
            setShowDeleteConfirm(true);
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false);
        setEntryToDelete(null);
    };

    if (!serverData.entries || Object.values(serverData.entries).length === 0) {
        return (
            <div className="p-5 text-center text-muted">
                <div className="mb-3" style={{ fontSize: '3rem' }}>💶</div>
                <h5>Keine externen Kosten vorhanden</h5>
                <p className="small">Klicke auf den "+" Button oben, um externe Kosten hinzuzufügen</p>
            </div>
        );
    }

    const entries = Object.values(serverData.entries);
    const total = serverData.calculation?.total || 0;

    return (
        <div className={`external-costs-invoice ${randomClassName}`}>
            <style>{`
                .${randomClassName} {
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                }
            `}</style>

            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover table-striped mb-0 align-middle" style={{fontSize: '0.875rem'}}>
                            <thead className="bg-light border-bottom" style={{position: 'sticky', top: 0, zIndex: 10}}>
                                <tr>
                                    <th className="py-2 px-3 text-muted fw-semibold" style={{width: '110px', fontSize: '0.8rem'}}>
                                        <div className="d-flex align-items-center">
                                            <i className="bi bi-calendar3 me-1"></i>
                                            <span className="text-nowrap">Datum</span>
                                        </div>
                                    </th>
                                    <th className="py-2 px-3 text-muted fw-semibold" style={{fontSize: '0.8rem'}}>
                                        <div className="d-flex align-items-center">
                                            <i className="bi bi-chat-left-text me-1"></i>
                                            <span className="text-nowrap">Beschreibung</span>
                                        </div>
                                    </th>
                                    <th className="py-2 px-3 text-muted fw-semibold" style={{width: '150px', fontSize: '0.8rem'}}>
                                        <div className="d-flex align-items-center">
                                            <i className="bi bi-receipt me-1"></i>
                                            <span className="text-nowrap">Rechnung</span>
                                        </div>
                                    </th>
                                    <th className="py-2 px-3 text-end text-muted fw-semibold" style={{width: '120px', fontSize: '0.8rem'}}>
                                        <div className="d-flex align-items-center justify-content-end">
                                            <i className="bi bi-currency-euro me-1"></i>
                                            <span className="text-nowrap">Betrag</span>
                                        </div>
                                    </th>
                                    <th className="py-2 px-3 text-center text-muted fw-semibold" style={{width: '80px', fontSize: '0.8rem'}}>
                                        <div className="d-flex align-items-center justify-content-center">
                                            <i className="bi bi-gear me-1"></i>
                                        </div>
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {entries.map((entry, index) => {
                                    const isExpanded = expandedDescriptions.includes(entry.id);
                                    const prevEntry = index > 0 ? entries[index - 1] : null;
                                    const showDateHeader = !prevEntry || prevEntry.date !== entry.date;
                                    
                                    return (
                                        <React.Fragment key={entry.id}>
                                            {showDateHeader && (
                                                <tr className="table-secondary">
                                                    <td colSpan="5" className="py-2 px-3 fw-semibold">
                                                        <i className="bi bi-calendar-day me-2"></i>
                                                        {moment(entry.date).format('dddd, DD.MM.YYYY')}
                                                    </td>
                                                </tr>
                                            )}
                                            <tr className={stylelist(
                                                [entry.accounted == 1, 'table-success'],
                                                [ExpenseBucket.addedIds.indexOf(entry.id) > -1, 'text-decoration-line-through']
                                            )}>
                                                <td className="py-2 px-3">
                                                    <div className="small text-muted">{moment(entry.date).format('DD.MM.YYYY')}</div>
                                                    <div className="small text-muted">{moment(entry.date).format('dddd')}</div>
                                                </td>
                                                <td className="py-2 px-3">
                                                    <div 
                                                        className={`${isExpanded ? '' : 'text-truncate'} d-flex align-items-start gap-2`}
                                                        onClick={() => toggleDescriptionExpand(entry.id)}
                                                        style={{ 
                                                            cursor: 'pointer',
                                                            maxWidth: isExpanded ? 'none' : '400px'
                                                        }}
                                                        title="Klicken zum Auf-/Zuklappen"
                                                    >
                                                        {!isExpanded && (
                                                            <i className="bi bi-currency-euro text-success flex-shrink-0 mt-1"></i>
                                                        )}
                                                        {isExpanded && (
                                                            <i className="bi bi-chevron-up text-primary flex-shrink-0 mt-1"></i>
                                                        )}
                                                        <span className="flex-grow-1">{entry.description}</span>
                                                    </div>
                                                    <div className="small text-muted mt-1">
                                                        {entry.person ? (
                                                            <PersonLabelWithDynamicInfo name={entry.person} />
                                                        ) : (
                                                            <span className="text-muted">Unbekannt</span>
                                                        )}
                                                        {entry.booked_by && entry.booked_by !== entry.person && (
                                                            <span className="ms-2">
                                                                • Gebucht von: <PersonLabelWithDynamicInfo name={entry.booked_by} />
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="py-2 px-3">
                                                    {entry.invoice_number ? (
                                                        <span className="badge bg-light text-dark border">
                                                            <i className="bi bi-file-earmark-text me-1"></i>
                                                            {entry.invoice_number}
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted small">—</span>
                                                    )}
                                                </td>
                                                <td className="py-2 px-3 text-end">
                                                    <strong className="text-nowrap">{formatCurrency(entry.amount)}</strong>
                                                    {entry.metadata?.ek_price != null && (
                                                        <div className="small text-muted">
                                                            EK: {formatCurrency(entry.metadata.ek_price)}
                                                            {entry.metadata.match_quality === 'matched' && (
                                                                <span 
                                                                    className="badge bg-success ms-1" 
                                                                    style={{fontSize: '0.65rem'}}
                                                                    title={`Match Score: ${entry.metadata.match_score || '-'}`}
                                                                >✓</span>
                                                            )}
                                                            {entry.metadata.match_quality === 'no_match' && (
                                                                <span 
                                                                    className="badge bg-warning text-dark ms-1" 
                                                                    style={{fontSize: '0.65rem'}}
                                                                    title="Kein VK-Match gefunden"
                                                                >⚠</span>
                                                            )}
                                                            {entry.metadata.match_quality === 'no_vk_data' && (
                                                                <span 
                                                                    className="badge bg-secondary ms-1" 
                                                                    style={{fontSize: '0.65rem'}}
                                                                    title="Keine VK-Daten"
                                                                >-</span>
                                                            )}
                                                        </div>
                                                    )}
                                                    {entry.metadata?.margin_net != null && (
                                                        <div 
                                                            className={`small ${entry.metadata.margin_net >= 0 ? 'text-success' : 'text-danger'}`}
                                                            style={{fontSize: '0.75rem'}}
                                                        >
                                                            Marge: {formatCurrency(entry.metadata.margin_net)}
                                                            {entry.metadata.margin_percentage != null && (
                                                                <span className="ms-1">({entry.metadata.margin_percentage}%)</span>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="py-2 px-3 text-center">
                                                    {!props.expenseBucket ? (
                                                        <button
                                                            className="btn btn-outline-danger"
                                                            onClick={() => handleDeleteClick(entry)}
                                                            title="Löschen"
                                                        >
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    ) : (
                                                        <>
                                                            {Boolean(ExpenseBucket.addedIds.indexOf(entry.id) < 0 && entry.accounted != 1) && (
                                                                <button
                                                                    className="btn btn-sm btn-success"
                                                                    onClick={() => { 
                                                                        // Add type to distinguish from expense entries
                                                                        const externalCostEntry = {
                                                                            ...entry,
                                                                            type: 'externalcost'
                                                                        };
                                                                        dispatch(add_entry(externalCostEntry));
                                                                    }}
                                                                    title="Abrechnen"
                                                                >
                                                                    <i className="bi bi-plus-circle me-1"></i>
                                                                    Abrechnen
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Total Footer */}
                    <div className="border-top bg-light p-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="text-muted">
                                <i className="bi bi-list-ul me-2"></i>
                                {entries.length} Position{entries.length !== 1 ? 'en' : ''}
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <span className="text-muted fw-semibold">Gesamtsumme:</span>
                                <span className="fs-5 fw-bold text-success">{formatCurrency(total)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <SlideToConfirm
                show={showDeleteConfirm}
                onConfirm={() => {
                    deleteEntry(entryToDelete.id);
                    setShowDeleteConfirm(false);
                    setEntryToDelete(null);
                }}
                onCancel={() => {
                    setShowDeleteConfirm(false);
                    setEntryToDelete(null);
                }}
                title="Externe Kosten löschen"
                message={entryToDelete ? `Möchten Sie diese Position wirklich löschen?\n\nBetrag: ${formatCurrency(entryToDelete.amount)}\nBeschreibung: ${entryToDelete.description}` : ''}
                slideText="Zum Löschen schieben"
                variant="danger"
                showBulkDeleteOption={deleteCount >= 1}
                onBulkDeleteToggle={(skip) => setSkipSliderForSession(skip)}
            />
        </div>
    );
};

export default ExternalCostsList;
